import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    EventEmitter,
    Injector,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    Output,
    SimpleChanges,
    ViewChild,
    ViewEncapsulation
} from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { ToastrService } from 'ngx-toastr';
import { forkJoin, of, Observable } from 'rxjs';
import { AppComponentBase } from '@shared/common/app-component-base';
import {
    AttachmentServiceProxy,
    FileParameter,
    OneDriveServiceProxy,
    OneDriveUploadOutputDtoIHandleError,
    ProjectFolderDto,
    ProjectFolderDtoListIHandleError,
    TaskAttachmentListIHandleError
} from '@shared/service-proxies/service-proxies';

export interface ImageUploadResult {
    success: boolean;
    copyLinkToDescription: boolean;
    fileName: string;
    fileSize: number;
    /** SharePoint / OneDrive web URL — full image. */
    webUrl: string;
    /** Direct download / thumbnail URL the editor will render in the card. */
    thumbnailUrl: string;
    fileId: string;
    /** Parent folder's Graph item ID — used to open the containing folder in
     *  Navigate-Sub-Folders so the uploaded file shows in the listing. */
    folderId: string;
    driveId: string;
    siteId: string;
    /** Folder the upload landed in — e.g. "Trovve / User Stories". */
    folderBreadcrumb: string;
    /** Raw upload response from the OneDrive service. */
    raw: any;
}

interface FolderRow {
    kind: 'onedrive' | 'folder';
    label: string;
    breadcrumb: string;
    isMain: boolean;
    /** True for folders attached to the current task (via getAttachmentByTaskId).
     *  Drives the "Task folder" badge in the template. A folder that is BOTH a
     *  project folder and a task attachment is deduped to the project version,
     *  so this flag is set only for task-only folders. */
    isTaskFolder: boolean;
    folder: ProjectFolderDto | null;
}

const ONEDRIVE_ROW: FolderRow = {
    kind: 'onedrive',
    label: 'OneDrive',
    breadcrumb: '',
    isMain: false,
    isTaskFolder: false,
    folder: null
};

@Component({
    selector: 'vf-image-upload-modal',
    templateUrl: './image-upload-modal.component.html',
    styleUrls: ['./image-upload-modal.component.scss'],
    // Encapsulation.None so the backdrop styles still apply after the element is
    // portaled to <body>. All selectors use the `vf-img-upload-*` prefix to avoid
    // polluting global CSS.
    encapsulation: ViewEncapsulation.None
})
export class ImageUploadModalComponent extends AppComponentBase
    implements OnChanges, AfterViewInit, OnDestroy {

    @Input() open: boolean = false;
    @Input() file: File | null = null;
    @Input() projectId: number | null = null;
    /** Task ID — when set, the modal also fetches folders attached to this task
     *  (TaskAttachment rows where fileType === 'folder' || 'documentLibrary')
     *  and shows them as additional suggested save-locations alongside the
     *  project folders. Pass null/0 when there's no saved task yet. */
    @Input() taskId: number | null = null;

    @Output() closeModal: EventEmitter<void> = new EventEmitter<void>();
    @Output() uploadComplete: EventEmitter<ImageUploadResult> = new EventEmitter<ImageUploadResult>();

    @ViewChild('backdropEl', { static: true }) backdropEl: ElementRef<HTMLDivElement> | null = null;

    public folders: FolderRow[] = [];
    public selectedRow: FolderRow | null = null;
    public copyLinkToDescription: boolean = true;
    public isLoadingFolders: boolean = false;
    public isUploading: boolean = false;
    /** SafeUrl wrapping the blob URL — Angular's URL sanitizer prefixes `blob:` with
     *  `unsafe:` by default, which makes <img> fail with ERR_UNKNOWN_URL_SCHEME. */
    public previewUrl: SafeUrl | null = null;

    /** Folder picked via "Browse other locations" — shown as a sticky selected row above the list. */
    public browsedFolder: FolderRow | null = null;
    /** Toggles the vf-file-selector overlay open. */
    public showBrowseFolderSelector: boolean = false;

    private _portaled: boolean = false;
    /** Raw blob URL retained so we can revokeObjectURL() it on close/destroy. */
    private _rawPreviewUrl: string | null = null;
    /** Composite key (`projectId|taskId`) of the last fetch — avoids re-fetching
     *  folders on every modal open when neither input has changed. */
    private _foldersCachedKey: string | null = null;
    /** Interval used to drive CD on the file-selector subtree while it's open
     *  (works around OnPush ancestors blocking propagation from its async HTTP). */
    private _fileSelectorTickId: any = null;

    constructor(
        injector: Injector,
        private _attachmentService: AttachmentServiceProxy,
        private _oneDriveService: OneDriveServiceProxy,
        private _toastr: ToastrService,
        private _sanitizer: DomSanitizer,
        private _cdr: ChangeDetectorRef,
        private _ngZone: NgZone
    ) {
        super(injector);
    }

    /**
     * Re-enters Angular zone (if needed) and forces a local CD pass on the modal
     * subtree. Used after every state mutation because the modal lives inside a
     * portaled element AND its parent (tiptap-editor) is OnPush — Angular's
     * auto-marking doesn't always reach us when events originate from nested
     * third-party widgets (e.g. the file-selector's bsModal events).
     */
    private _tick(): void {
        const run = () => {
            try { this._cdr.detectChanges(); } catch { /* component may be destroyed */ }
        };
        if (NgZone.isInAngularZone()) {
            run();
        } else {
            this._ngZone.run(run);
        }
    }

    ngAfterViewInit(): void {
        this._portalToBody();
    }

    ngOnDestroy(): void {
        this._releasePreview();
        this._removeFromBody();
        this._stopFileSelectorTicks();
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['open']) {
            if (this.open) {
                this._onOpen();
            } else {
                this._releasePreview();
            }
        }
        if (changes['file'] && this.file && this.open) {
            this._buildPreview(this.file);
        }
    }

    /**
     * Move the backdrop element to <body> so `position: fixed` is not constrained
     * by any transformed/filtered ancestor in the editor's component tree.
     */
    private _portalToBody(): void {
        if (this._portaled) {
            return;
        }
        const el = this.backdropEl?.nativeElement;
        if (el && document.body) {
            document.body.appendChild(el);
            this._portaled = true;
        }
    }

    private _removeFromBody(): void {
        const el = this.backdropEl?.nativeElement;
        if (el && el.parentNode === document.body) {
            try { document.body.removeChild(el); } catch { /* ignore */ }
        }
    }

    private _onOpen(): void {
        let hasProject: boolean = false;
        let hasTask: boolean = false;
        let cacheKey: string = '';

        this.copyLinkToDescription = true;
        this.isUploading = false;
        this.browsedFolder = null;
        this.showBrowseFolderSelector = false;

        if (this.file) {
            this._buildPreview(this.file);
        }

        hasProject = !!(this.projectId && this.projectId > 0);
        hasTask = !!(this.taskId && this.taskId > 0);
        cacheKey = (hasProject ? String(this.projectId) : '0') + '|' + (hasTask ? String(this.taskId) : '0');

        if (hasProject || hasTask) {
            // Reuse cached folder list when neither input changed since last open.
            if (this._foldersCachedKey === cacheKey && this.folders.length > 0) {
                this.selectedRow = this._defaultSelection(this.folders);
                this._tick();
                return;
            }
            this.folders = [];
            this.selectedRow = null;
            this._loadSuggestedFolders(hasProject ? this.projectId : null, hasTask ? this.taskId : null, cacheKey);
        } else {
            this.folders = [ONEDRIVE_ROW];
            this.selectedRow = ONEDRIVE_ROW;
            this._foldersCachedKey = null;
        }
        this._tick();
    }

    /**
     * Fetches project folders (`getProjectFolder`) and task-attached folders
     * (`getAttachmentByTaskId`, filtered to `fileType === 'folder' ||
     * 'documentLibrary'`) in parallel, dedupes task folders against project
     * folders by keyId, and rebuilds the suggested-folder list. The Main project
     * folder leads; task-only folders follow, then the remaining project folders,
     * and OneDrive brings up the rear.
     */
    private _loadSuggestedFolders(projectId: number | null, taskId: number | null, cacheKey: string): void {
        let projectFolders$: Observable<ProjectFolderDtoListIHandleError | null>;
        let taskFolders$: Observable<TaskAttachmentListIHandleError | null>;

        projectFolders$ = projectId && projectId > 0
            ? this._attachmentService.getProjectFolder(projectId)
            : of<ProjectFolderDtoListIHandleError | null>(null);
        taskFolders$ = taskId && taskId > 0
            ? this._attachmentService.getAttachmentByTaskId(taskId)
            : of<TaskAttachmentListIHandleError | null>(null);

        this.isLoadingFolders = true;
        forkJoin([projectFolders$, taskFolders$]).subscribe(
            ([projectResult, taskResult]) => {
                let mainRow: FolderRow | null = null;
                const projectRows: FolderRow[] = [];
                const taskOnlyRows: FolderRow[] = [];
                const seenKeys: Set<string> = new Set<string>();
                const list: FolderRow[] = [];

                this.isLoadingFolders = false;

                if (projectResult && !projectResult.isError && projectResult.responseData) {
                    projectResult.responseData.forEach(folder => {
                        const row: FolderRow = {
                            kind: 'folder',
                            label: folder.folderName || '',
                            breadcrumb: folder.folderPath || '',
                            isMain: !!folder.isDefault,
                            isTaskFolder: false,
                            folder
                        };
                        const dedupKey: string = (folder.keyId || folder.msFolderId || '').toLowerCase();
                        if (dedupKey) {
                            seenKeys.add(dedupKey);
                        }
                        if (row.isMain && !mainRow) {
                            mainRow = row;
                        }
                        projectRows.push(row);
                    });
                }

                if (taskResult && !taskResult.isError && taskResult.responseData) {
                    taskResult.responseData.forEach(att => {
                        const ft: string = (att.fileType || '').toLowerCase();
                        const dedupKey: string = (att.keyId || att.msFolderId || '').toLowerCase();
                        let synthFolder: ProjectFolderDto | null = null;
                        let row: FolderRow | null = null;

                        if (ft !== 'folder' && ft !== 'documentlibrary') {
                            // Skip file attachments — only folder-typed attachments
                            // are valid save-locations.
                            return;
                        }
                        if (att.isHide) {
                            return;
                        }
                        if (dedupKey && seenKeys.has(dedupKey)) {
                            // Already present as a project folder — keep the project
                            // version (it carries the Main badge / isDefault flag).
                            return;
                        }
                        if (dedupKey) {
                            seenKeys.add(dedupKey);
                        }

                        synthFolder = new ProjectFolderDto({
                            id: 0,
                            keyId: att.keyId || '',
                            folderName: att.fileName || '',
                            folderPath: att.filePath || '',
                            fileType: att.fileType || 'folder',
                            // Heuristic: a missing site ID means OneDrive personal,
                            // anything else is a SharePoint document library.
                            isOneDrive: !att.msSiteId,
                            isDefault: false,
                            msDriveId: att.msDriveId || '',
                            msSiteId: att.msSiteId || '',
                            msFolderId: att.msFolderId || att.keyId || ''
                        });
                        row = {
                            kind: 'folder',
                            label: att.fileName || '',
                            breadcrumb: att.filePath || '',
                            isMain: false,
                            isTaskFolder: true,
                            folder: synthFolder
                        };
                        taskOnlyRows.push(row);
                    });
                }

                // Final order: Main project folder → task-only folders → other project folders → OneDrive.
                // OneDrive is LAST so the project's own folders are what the eye lands on first: it used to
                // head the list and be pre-selected while the project folders were still resolving, so a
                // file dropped into task notes got saved to OneDrive by accident.
                if (mainRow) {
                    list.push(mainRow);
                }
                taskOnlyRows.forEach(r => list.push(r));
                projectRows.forEach(r => {
                    if (r !== mainRow) {
                        list.push(r);
                    }
                });
                list.push(ONEDRIVE_ROW);

                this.folders = list;
                this.selectedRow = this._defaultSelection(list);
                this._foldersCachedKey = cacheKey;
                this._tick();
            },
            () => {
                this.isLoadingFolders = false;
                this.folders = [ONEDRIVE_ROW];
                this.selectedRow = ONEDRIVE_ROW;
                this._foldersCachedKey = null;
                this._tick();
            }
        );
    }

    /**
     * The row a freshly-opened popup starts on: the project's Main folder if one is set, else the first
     * project/task folder in the list. OneDrive is only the default when the task has no folder to offer
     * (no project linked, or a project with no attached folders) — it must never win over a real project
     * folder, which is what put files in the wrong place.
     */
    private _defaultSelection(list: FolderRow[]): FolderRow | null {
        return list.find(r => r.isMain)
            || list.find(r => r.kind === 'folder')
            || list[0]
            || null;
    }

    /** True when the pending file is an image — drives the thumbnail vs type-icon
     *  split in the template. Non-image uploads (docx/pdf/xlsx/…) have nothing to
     *  preview, so they show a lucide icon chosen by extension instead. */
    public get isImageFile(): boolean {
        return !!(this.file && this.file.type && this.file.type.indexOf('image/') === 0);
    }

    /** Hosted brand icon for Office types — the same CDN set `FileIconComponent`
     *  uses in the Files tab — or '' when the type has no brand icon and the
     *  lucide fallback below should render instead. */
    public get fileBrandIconUrl(): string {
        const base: string = 'https://cdn.trovve.com/vfapp/images/file-icons/';
        const name: string = (this.file && this.file.name) || '';
        const dot: number = name.lastIndexOf('.');
        const ext: string = dot >= 0 ? name.substring(dot + 1).toLowerCase() : '';

        switch (ext) {
            case 'doc':
            case 'docx':
                return base + 'word.svg';
            case 'xls':
            case 'xlsx':
                return base + 'excel.svg';
            case 'ppt':
            case 'pptx':
                return base + 'powerpoint.svg';
            case 'vsd':
            case 'vsdx':
                return base + 'visio.svg';
            default:
                return '';
        }
    }

    /** Lucide fallback name. Restricted to icons registered in AppCommonModule's
     *  `LucideAngularModule.pick(...)` — an unregistered name renders blank. */
    public get fileIconName(): string {
        return this.isImageFile ? 'image' : 'file-text';
    }

    private _buildPreview(file: File): void {
        this._releasePreview();
        // Only images get a blob thumbnail. Handing a docx/pdf blob to <img> just
        // fires an error and falls back anyway — skip the wasted object URL.
        if (!file || !file.type || file.type.indexOf('image/') !== 0) {
            return;
        }
        try {
            const raw: string = URL.createObjectURL(file);
            this._rawPreviewUrl = raw;
            // Wrap as SafeUrl — Angular's default sanitizer marks `blob:` URLs as
            // unsafe and prefixes them with `unsafe:`, which makes <img> fail with
            // net::ERR_UNKNOWN_URL_SCHEME. Bypassing is safe here: the URL came
            // from a File the user just dropped/pasted in this browser tab.
            this.previewUrl = this._sanitizer.bypassSecurityTrustUrl(raw);
        } catch {
            this._rawPreviewUrl = null;
            this.previewUrl = null;
        }
    }

    private _releasePreview(): void {
        if (this._rawPreviewUrl) {
            try {
                URL.revokeObjectURL(this._rawPreviewUrl);
            } catch {
                // ignore
            }
            this._rawPreviewUrl = null;
        }
        this.previewUrl = null;
    }

    public selectRow(row: FolderRow): void {
        if (this.isUploading) {
            return;
        }
        this.selectedRow = row;
        this._tick();
    }

    // --- Browse other locations ---

    public openBrowseFolder(): void {
        if (this.isUploading) {
            return;
        }
        this.showBrowseFolderSelector = true;
        this._tick();
        this._startFileSelectorTicks();
    }

    public onBrowseFolderClose(): void {
        this.showBrowseFolderSelector = false;
        this._tick();
        this._stopFileSelectorTicks();
    }

    /**
     * Pump a periodic detectChanges() while vf-file-selector is open. The selector
     * runs HTTP calls (SharePoint sites list, folder listing) whose subscribe
     * callbacks update its local state — but our OnPush ancestor (tiptap-editor)
     * doesn't get auto-marked from inside a third-party widget's async flow, so
     * the file-selector's UI freezes on a skeleton until the user does anything
     * else. Polling detectChanges() on the modal subtree (which includes the
     * file-selector) keeps it repainting. Registered outside the zone so the
     * interval itself doesn't schedule extra global CD.
     */
    private _startFileSelectorTicks(): void {
        this._stopFileSelectorTicks();
        this._ngZone.runOutsideAngular(() => {
            this._fileSelectorTickId = setInterval(() => {
                try { this._cdr.detectChanges(); } catch { /* destroyed */ }
            }, 150);
        });
    }

    private _stopFileSelectorTicks(): void {
        if (this._fileSelectorTickId !== null) {
            clearInterval(this._fileSelectorTickId);
            this._fileSelectorTickId = null;
        }
    }

    /**
     * vf-file-selector emits `selectedItems` as a tuple: [items[], siteId].
     * For single-folder mode the user-selected folder is items[0].
     */
    public onBrowseFolderSelected(ev: any): void {
        this.showBrowseFolderSelector = false;
        if (!ev || !ev[0] || !ev[0].length) {
            return;
        }
        const element: any = ev[0][0];
        if (!element) {
            return;
        }

        let driveType: string = '';
        let driveId: string = '';
        let keyId: string = '';
        let siteId: string = '';
        let isOneDrive: boolean = false;
        let label: string = '';
        let breadcrumb: string = '';

        label = element.name || '';
        driveId = (element.parentReference && element.parentReference.driveId) || element.id || '';
        keyId = element.parentReference ? element.id : '';
        siteId = (element.parentReference && element.parentReference.siteId)
            || (ev[1] || '')
            || '';
        driveType = (element.parentReference && element.parentReference.driveType)
            || 'documentLibrary';
        isOneDrive = driveType.toLowerCase() === 'business';

        // Build breadcrumb from webUrl (matches the existing edit-task-modal pattern)
        const webUrl: string = element.webUrl || '';
        const parts: string[] = [];
        try {
            const urlPath = new URL(webUrl).pathname;
            const segs = urlPath.split('/').filter(s => !!s);
            if (isOneDrive) {
                const docIdx = segs.findIndex(p => p.toLowerCase() === 'documents');
                const tail = docIdx >= 0 ? segs.slice(docIdx) : [label];
                tail.forEach(s => parts.push(decodeURIComponent(s)));
            } else {
                const siteIdx = segs.findIndex(p => p.toLowerCase() === 'sites');
                const tail = siteIdx >= 0 ? segs.slice(siteIdx + 1) : [label];
                tail.forEach(s => parts.push(decodeURIComponent(s)));
            }
        } catch {
            parts.push(label);
        }
        breadcrumb = parts.join(' > ');

        const synthFolder: ProjectFolderDto = new ProjectFolderDto({
            id: 0,
            keyId: keyId || driveId,
            folderName: label,
            folderPath: breadcrumb,
            fileType: '',
            isOneDrive,
            isDefault: false,
            msDriveId: driveId,
            msSiteId: siteId,
            msFolderId: keyId || driveId
        });

        this.browsedFolder = {
            kind: 'folder',
            label,
            breadcrumb,
            isMain: false,
            isTaskFolder: false,
            folder: synthFolder
        };
        this.selectedRow = this.browsedFolder;
        this._tick();
        this._stopFileSelectorTicks();
    }

    /**
     * Splits a breadcrumb string into its segments. Backend folder paths use
     * ` / ` as the separator (e.g. "FlowerDay Law team / ... / Lopez Project"),
     * but some legacy/manual paths use ` > ` — accept both.
     */
    public splitBreadcrumb(value: string): string[] {
        if (!value) {
            return [];
        }
        return value.split(/ [>/] /).map(s => s.trim()).filter(s => s.length > 0);
    }

    public formatSize(bytes: number): string {
        let size = 0;
        if (!bytes || bytes <= 0) {
            return '';
        }
        if (bytes < 1024) {
            return bytes + ' B';
        }
        if (bytes < 1024 * 1024) {
            size = bytes / 1024;
            return Math.round(size) + ' KB';
        }
        size = bytes / (1024 * 1024);
        return size.toFixed(1) + ' MB';
    }

    public onCancel(): void {
        if (this.isUploading) {
            return;
        }
        this.closeModal.emit();
        this._tick();
    }

    /** Fallback: if the blob preview fails to load, drop back to the placeholder icon. */
    public onPreviewError(): void {
        this._releasePreview();
        this._tick();
    }

    public onBackdropClick(ev: MouseEvent): void {
        if (this.isUploading) {
            return;
        }
        if (ev.target === this.backdropEl?.nativeElement) {
            this.closeModal.emit();
        }
    }

    public onUpload(): void {
        if (!this.file || !this.selectedRow || this.isUploading) {
            return;
        }

        let driveId: string | null = null;
        let keyId: string | null = null;
        let folderBreadcrumb: string = '';
        let siteId: string = '';

        if (this.selectedRow.kind === 'folder' && this.selectedRow.folder) {
            const folder = this.selectedRow.folder;
            driveId = folder.msDriveId || null;
            keyId = folder.keyId || null;
            folderBreadcrumb = folder.folderPath || folder.folderName || '';
            siteId = folder.msSiteId || '';
            // OneDrive root upload: when keyId === driveId, pass keyId=null (signals "root").
            if (driveId && keyId && driveId === keyId) {
                keyId = null;
            }
        } else if (this.selectedRow.kind === 'onedrive') {
            // Show a meaningful path for files dropped into OneDrive personal root.
            folderBreadcrumb = 'OneDrive';
        }

        this.isUploading = true;
        this._tick();
        const fileParam: FileParameter = { data: this.file, fileName: this.file.name };

        // DuplicateAction = 1 (rename) — clipboard pastes all arrive named
        // "image.png", so failing on a name clash (action 0) made the 2nd-and-later
        // paste into the same folder error out with Graph "nameAlreadyExists" → "101".
        // Auto-rename instead (same behavior the task-attachment upload path uses).
        this._oneDriveService.uploadFile(fileParam, driveId as any, keyId as any, 1).subscribe(
            (result: OneDriveUploadOutputDtoIHandleError) => {
                this.isUploading = false;
                if (result.isError || !result.responseData) {
                    this._toastr.error("Couldn't upload file. Try again.", null, this.toastrOptions);
                    this._tick();
                    return;
                }
                const data: any = result.responseData;
                const webUrl: string = data.webUrl || '';
                // Prefer @microsoft.graph.downloadUrl — it streams raw bytes with the
                // correct image MIME type, so <img src> works for OneDrive personal
                // root uploads (where webUrl is a viewer page, not the file content).
                const thumb: string = data.downloadUrl || webUrl;
                // OneDriveUploadOutputDto is a FLAT DTO (no parentReference).
                // Read driveId / folderId / siteId straight off it. The local
                // variables are only used as a fallback for older API responses.
                const out: ImageUploadResult = {
                    success: true,
                    copyLinkToDescription: this.copyLinkToDescription,
                    fileName: data.name || this.file?.name || '',
                    fileSize: this.file ? this.file.size : 0,
                    webUrl,
                    thumbnailUrl: thumb,
                    fileId: data.id || '',
                    folderId: data.folderId || '',
                    driveId: data.driveId || driveId || '',
                    siteId: data.siteId || siteId || '',
                    folderBreadcrumb,
                    raw: data
                };
                this.uploadComplete.emit(out);
                this._tick();
            },
            () => {
                this.isUploading = false;
                this._toastr.error("Couldn't upload image. Try again.", null, this.toastrOptions);
                this._tick();
            }
        );
    }
}
